import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';
const ServicesManagement = () => {
    const [services, setServices] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [editingService, setEditingService] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        icon: '',
        slug: '',
        order: 0,
        isActive: true,
    });
    const { toast } = useToast();
    useEffect(() => {
        fetchServices();
    }, []);
    const fetchServices = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${API_URL}/services/all`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await response.json();
            setServices(data);
        }
        catch (error) {
            toast({ title: 'Error', description: 'Failed to fetch services', variant: 'destructive' });
        }
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        try {
            const url = editingService ? `${API_URL}/services/${editingService.id}` : `${API_URL}/services`;
            const method = editingService ? 'PUT' : 'POST';
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(formData),
            });
            if (!response.ok)
                throw new Error('Failed to save service');
            toast({ title: 'Success', description: `Service ${editingService ? 'updated' : 'created'} successfully` });
            setIsOpen(false);
            resetForm();
            fetchServices();
        }
        catch (error) {
            toast({ title: 'Error', description: 'Failed to save service', variant: 'destructive' });
        }
    };
    const handleDelete = async (id) => {
        if (!confirm('Are you sure you want to delete this service?'))
            return;
        const token = localStorage.getItem('token');
        try {
            await fetch(`${API_URL}/services/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
            toast({ title: 'Success', description: 'Service deleted successfully' });
            fetchServices();
        }
        catch (error) {
            toast({ title: 'Error', description: 'Failed to delete service', variant: 'destructive' });
        }
    };
    const handleEdit = (service) => {
        setEditingService(service);
        setFormData({
            title: service.title,
            description: service.description,
            icon: service.icon,
            slug: service.slug || '',
            order: service.order,
            isActive: service.isActive,
        });
        setIsOpen(true);
    };
    const resetForm = () => {
        setEditingService(null);
        setFormData({ title: '', description: '', icon: '', slug: '', order: 0, isActive: true });
    };
    return (_jsxs("div", { children: [_jsxs("div", { className: "flex justify-between items-center mb-8", children: [_jsx("h1", { className: "text-3xl font-bold", children: "Services Management" }), _jsxs(Dialog, { open: isOpen, onOpenChange: (open) => { setIsOpen(open); if (!open)
                            resetForm(); }, children: [_jsx(DialogTrigger, { asChild: true, children: _jsxs(Button, { children: [_jsx(Plus, { className: "mr-2 h-4 w-4" }), "Add Service"] }) }), _jsxs(DialogContent, { className: "max-w-2xl", children: [_jsxs(DialogHeader, { children: [_jsx(DialogTitle, { children: editingService ? 'Edit Service' : 'Add New Service' }), _jsx(DialogDescription, { children: "Manage your service offerings" })] }), _jsxs("form", { onSubmit: handleSubmit, className: "space-y-4", children: [_jsxs("div", { children: [_jsx(Label, { htmlFor: "title", children: "Title" }), _jsx(Input, { id: "title", value: formData.title, onChange: (e) => setFormData({ ...formData, title: e.target.value }), required: true })] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "description", children: "Description" }), _jsx(Textarea, { id: "description", value: formData.description, onChange: (e) => setFormData({ ...formData, description: e.target.value }), required: true })] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "icon", children: "Icon Name (lucide-react)" }), _jsx(Input, { id: "icon", placeholder: "Code", value: formData.icon, onChange: (e) => setFormData({ ...formData, icon: e.target.value }), required: true })] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "slug", children: "Page Slug (URL)" }), _jsx(Input, { id: "slug", placeholder: "kataskevi-istoselidon", value: formData.slug, onChange: (e) => setFormData({ ...formData, slug: e.target.value }), required: true }), _jsx("p", { className: "text-sm text-muted-foreground mt-1", children: "The page URL where this service links to (e.g., kataskevi-istoselidon)" })] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "order", children: "Order" }), _jsx(Input, { id: "order", type: "number", value: formData.order, onChange: (e) => setFormData({ ...formData, order: parseInt(e.target.value) }) })] }), _jsxs("div", { className: "flex items-center space-x-2", children: [_jsx(Switch, { id: "isActive", checked: formData.isActive, onCheckedChange: (checked) => setFormData({ ...formData, isActive: checked }) }), _jsx(Label, { htmlFor: "isActive", children: "Active" })] }), _jsxs(Button, { type: "submit", className: "w-full", children: [editingService ? 'Update' : 'Create', " Service"] })] })] })] })] }), _jsx("div", { className: "grid gap-4", children: services.map((service) => (_jsxs(Card, { children: [_jsx(CardHeader, { children: _jsxs(CardTitle, { className: "flex justify-between items-center", children: [_jsx("span", { children: service.title }), _jsxs("div", { className: "flex gap-2", children: [_jsx(Button, { size: "sm", variant: "outline", onClick: () => handleEdit(service), children: _jsx(Pencil, { className: "h-4 w-4" }) }), _jsx(Button, { size: "sm", variant: "destructive", onClick: () => handleDelete(service.id), children: _jsx(Trash2, { className: "h-4 w-4" }) })] })] }) }), _jsxs(CardContent, { children: [_jsx("p", { className: "text-muted-foreground mb-2", children: service.description }), _jsxs("div", { className: "flex gap-4 text-sm", children: [_jsxs("span", { children: ["Icon: ", service.icon] }), _jsxs("span", { children: ["Order: ", service.order] }), _jsxs("span", { children: ["Status: ", service.isActive ? 'Active' : 'Inactive'] })] })] })] }, service.id))) })] }));
};
export default ServicesManagement;
